import { useCallback, useEffect, useMemo, useState } from 'react';
import { trpClient } from '../client/trpClient';
import { useAuth } from '../contexts/AuthContext';
import {
    DashboardRFQ, RFQStage, isActiveRfq, mapRfq, PAGE_LIMIT,
} from './dashboardMappers';

/**
 * Full org RFQ list behind the /rfqs page — same org-wide `GET /trp/rfqs`
 * endpoint and row mapping as the dashboard, but with the complete filter set
 * the dashboard deliberately omits: unlike "Active RFQs" there, this list
 * shows EVERY status, so awarded and cancelled RFQs finally have a home.
 * Every filter is backed by a stored field — nothing derived from data the
 * list item doesn't carry.
 */
export type RfqStatusFilter = 'all' | 'active' | 'awarded' | 'cancelled';
export type RfqStageFilter = 'all' | RFQStage;
export type RfqOwnerFilter = 'everyone' | 'mine';
export type RfqSortKey = 'name' | 'stage' | 'parts' | 'created' | 'updated';
export interface RfqSort {
    key: RfqSortKey;
    direction: 'asc' | 'desc';
}

/** Freshest activity first — the natural reading order for a work list. */
export const DEFAULT_SORT: RfqSort = { key: 'updated', direction: 'desc' };

export interface RfqStatusCounts {
    all: number;
    active: number;
    awarded: number;
    cancelled: number;
}

const matchesStatus = (rfq: DashboardRFQ, status: RfqStatusFilter): boolean => {
    if (status === 'all') return true;
    if (status === 'active') return isActiveRfq(rfq);
    if (status === 'awarded') return rfq.awarded;
    return rfq.status === 'CANCELLED';
};

const sortValue = (rfq: DashboardRFQ, key: RfqSortKey): string | number => {
    switch (key) {
        case 'name': return (rfq.name ?? rfq.id).toLowerCase();
        case 'stage': return rfq.stageStep;
        case 'parts': return rfq.partsCount;
        case 'created': return new Date(rfq.createdAtIso).getTime();
        case 'updated': return new Date(rfq.updatedAtIso).getTime();
    }
};

export const useRfqList = () => {
    const { backendUser } = useAuth();
    const myUserId = backendUser?.userId;
    const myEmail = backendUser?.email;

    const [rfqs, setRfqs] = useState<DashboardRFQ[]>([]);
    const [orgTotal, setOrgTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<RfqStatusFilter>('all');
    const [stage, setStage] = useState<RfqStageFilter>('all');
    const [owner, setOwner] = useState<RfqOwnerFilter>('everyone');
    const [sort, setSort] = useState<RfqSort>(DEFAULT_SORT);

    const fetchRfqs = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await trpClient.getRFQs(1, PAGE_LIMIT);
            if (response.success) {
                setRfqs(response.data.items.map((rfq) => mapRfq(rfq, myUserId, myEmail)));
                setOrgTotal(response.data.pagination.total);
            }
        } catch (err: unknown) {
            console.error('Failed to fetch RFQ list:', err);
            setError(err instanceof Error ? err.message : 'Failed to load RFQs');
        } finally {
            setLoading(false);
        }
    }, [myUserId, myEmail]);

    useEffect(() => {
        fetchRfqs();
    }, [fetchRfqs]);

    // Everything EXCEPT the status dimension — the status chips show counts
    // inside the current search/stage/owner scope, so a chip's number always
    // equals exactly what clicking it will show.
    const scoped = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return rfqs.filter((rfq) => {
            if (owner === 'mine' && !rfq.isMine) return false;
            if (stage !== 'all' && rfq.stage !== stage) return false;
            if (!needle) return true;
            return (
                (rfq.name ?? '').toLowerCase().includes(needle) ||
                rfq.id.toLowerCase().includes(needle)
            );
        });
    }, [rfqs, search, stage, owner]);

    const statusCounts = useMemo<RfqStatusCounts>(() => ({
        all: scoped.length,
        active: scoped.filter(isActiveRfq).length,
        awarded: scoped.filter((rfq) => rfq.awarded).length,
        cancelled: scoped.filter((rfq) => rfq.status === 'CANCELLED').length,
    }), [scoped]);

    const rows = useMemo(() => {
        const dir = sort.direction === 'asc' ? 1 : -1;
        return scoped
            .filter((rfq) => matchesStatus(rfq, status))
            .sort((a, b) => {
                const va = sortValue(a, sort.key);
                const vb = sortValue(b, sort.key);
                return va < vb ? -dir : va > vb ? dir : 0;
            });
    }, [scoped, status, sort]);

    const filtersApplied =
        search.trim() !== '' || status !== 'all' || stage !== 'all' || owner !== 'everyone';

    const clearFilters = useCallback(() => {
        setSearch('');
        setStatus('all');
        setStage('all');
        setOwner('everyone');
    }, []);

    return {
        rows,
        loading,
        error,
        refresh: fetchRfqs,
        /** Org-wide total from the server, beyond the fetched page. */
        orgTotal,
        /** True when the org holds more RFQs than the fetched page covers. */
        truncated: orgTotal > rfqs.length,
        /** Active count from the fetched page — the sidebar badge number. */
        activeCount: rfqs.filter(isActiveRfq).length,
        hasAnyRfqs: rfqs.length > 0,
        search, setSearch,
        status, setStatus,
        stage, setStage,
        owner, setOwner,
        sort, setSort,
        statusCounts,
        filtersApplied,
        clearFilters,
    };
};
